import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getPublicDelivery, downloadFile } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { StatusDot } from '@/components/ui/status-dot';
import { DocumentMark } from '@/components/ui/document-mark';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

export default function PublicDelivery() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPublicDelivery(token).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [token]);

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleDownload = async (deliverable) => {
    try {
      const res = await downloadFile(deliverable.file_path);
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = deliverable.file_name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Download failed');
    }
  };

  if (loading) return <div className="min-h-screen bg-paper flex items-center justify-center"><p className="text-small text-ink-muted">Loading…</p></div>;
  if (!data) return <div className="min-h-screen bg-paper flex items-center justify-center"><p className="text-small text-ink-muted">Not found</p></div>;

  const files = data.deliverables || [];
  const fileCount = files.length;
  const unlockedCount = files.filter(d => !d.is_locked).length;
  const lockedCount = fileCount - unlockedCount;

  /* Files can now be gated behind different milestone invoices, so the page is
     no longer all-or-nothing: some may be downloadable while others wait. */
  const heading = fileCount === 0
    ? 'No deliverables yet'
    : lockedCount === 0
      ? 'Your files are ready'
      : unlockedCount === 0
        ? 'Payment required'
        : 'Your files';

  /* Describes the gate on a locked file — the milestone name where one was set,
     otherwise the invoice number. */
  const gateLabel = (d) => {
    if (!d.gated_by) return 'Locked until paid';
    const { milestone_label, invoice_number } = d.gated_by;
    return milestone_label
      ? `Unlocks with ${milestone_label}`
      : `Unlocks with invoice #${invoice_number}`;
  };

  return (
    <div className="min-h-screen bg-paper px-6 py-12 md:py-20" data-testid="public-delivery-page">
      <div className="mx-auto max-w-[720px]">
        <DocumentMark freelancer={data.freelancer} />

        <div className="mt-10" data-testid="delivery-card">
          <h1 className="font-display text-display-l text-ink">{heading}</h1>
          {data.freelancer?.business_name && (
            <p className="mt-3 text-body text-ink-muted">From {data.freelancer.business_name}</p>
          )}
          {fileCount > 0 && lockedCount > 0 && (
            <p className="mt-1 text-small tabular-nums text-ink-subtle">
              {unlockedCount} of {fileCount} available
            </p>
          )}

          {fileCount === 0 ? (
            <EmptyState
              className="mt-10"
              title="Nothing here yet"
              description="Your files will appear here as soon as they are uploaded."
            />
          ) : (
            <div className="mt-10">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>File</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Size</TableHead>
                    <TableHead className="text-right">Download</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {files.map((d, i) => (
                    <TableRow key={d.id || i} data-testid={`download-item-${i}`}>
                      <TableCell className="text-ink">{d.file_name}</TableCell>
                      <TableCell>
                        {d.is_locked
                          ? <span className="text-small text-ink-subtle">{gateLabel(d)}</span>
                          : <StatusDot status="unlocked" />}
                      </TableCell>
                      <TableCell className="text-right text-ink-muted">{formatFileSize(d.file_size)}</TableCell>
                      <TableCell className="text-right">
                        {d.is_locked ? (
                          d.gated_by?.token && d.gated_by.status !== 'paid' ? (
                            <a
                              href={`/pay/${d.gated_by.token}`}
                              className="text-small text-forest hover:text-forest-hover"
                            >
                              Pay to unlock
                            </a>
                          ) : (
                            <span className="text-small text-ink-subtle">—</span>
                          )
                        ) : (
                          <button
                            onClick={() => handleDownload(d)}
                            className="text-small text-forest hover:text-forest-hover"
                          >
                            Download
                          </button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {!data.is_paid && (
            <div className="mt-10 border-t border-line pt-8">
              <p className="text-small text-ink-muted">
                {data.milestone_label
                  ? `${data.milestone_label} is still outstanding.`
                  : 'This invoice is still outstanding.'}
              </p>
              <div className="mt-4">
                <Button size="lg" asChild data-testid="go-to-payment-btn">
                  <a href={`/pay/${data.invoice_token}`}>Go to payment</a>
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
